import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { useAuth } from './AuthContext';

const Card = ({ title, children, right, className = '' }) => (
  <div className={`bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 ${className}`}>
    <div className="flex justify-between items-center mb-3"><h2 className="text-white font-semibold">{title}</h2>{right}</div>
    {children}
  </div>
);
const In = ({ label, value, onChange, type = 'text', required }) => (
  <div><label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" /></div>
);
const CopyRow = ({ value }) => (
  <div className="flex gap-2 items-center">
    <input readOnly value={value} className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-[#00D4FF] text-xs" />
    <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(value)}
      className="px-3 py-1.5 text-sm bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Copy</button>
  </div>
);

const APP_URLS = { re: 'https://re.aida-advisory.com', cre: 'https://cre.aida-advisory.com' };

// Mint a single-use, target-bound switch code in the CURRENT app, then hand ONLY
// that code (not a token) to the other app's /switch landing. Replaces the old
// fragment-JWT handoff — no live credential ever touches the URL/browser history.
async function beginSwitch(otherKey) {
  try {
    const res = await authFetch(`${API}/api/auth/switch/create`, {
      method: 'POST',
      body: JSON.stringify({ target: otherKey }),
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data && data.code) {
      window.location.href = `${APP_URLS[otherKey]}/switch#code=${encodeURIComponent(data.code)}`;
    }
  } catch (e) {
    /* stay put on failure */
  }
}

// Cross-app toggle: shown only to DUAL-PRODUCT REGISTRY users. To see "Switch to
// CRE" while in RE you must have access to CRE too — i.e. you hold both products.
export function AppSwitcher({ user, compact }) {
  const isCRE = API.includes('cre.aida');
  const otherKey = isCRE ? 're' : 'cre';
  const otherLabel = isCRE ? 'RE' : 'CRE';
  const hasOther = isCRE ? user?.access_re : user?.access_cre;
  if (!hasOther || user?.account_type !== 'brokerage_user') return null;
  return (
    <button onClick={() => beginSwitch(otherKey)} title={`Switch to ${otherLabel}`}
      className="px-2 py-1.5 text-xs bg-[#243B55] text-[#00D4FF] rounded hover:bg-[#2d4a6b] shrink-0 whitespace-nowrap">
      {compact ? <>⇄ {otherLabel}</> : <>Switch to {otherLabel} →</>}
    </button>
  );
}

// For a broker viewing the console: jump to the app (current product) or the other app.
function AppOpenActions({ user }) {
  const isCRE = API.includes('cre.aida');
  const curLabel = isCRE ? 'CRE' : 'RE';
  const otherKey = isCRE ? 're' : 'cre';
  const otherLabel = isCRE ? 'RE' : 'CRE';
  const hasOther = (isCRE ? user?.access_re : user?.access_cre) && user?.account_type === 'brokerage_user';
  return (
    <>
      <a href="/" className="px-2.5 py-1.5 text-xs bg-[#00E676] text-[#0A0E27] font-semibold rounded hover:bg-[#00C853] shrink-0">Open SchemeSync {curLabel} →</a>
      {hasOther && <button onClick={() => beginSwitch(otherKey)} className="px-2.5 py-1.5 text-xs bg-[#243B55] text-[#00D4FF] rounded hover:bg-[#2d4a6b] shrink-0">Open SchemeSync {otherLabel} →</button>}
    </>
  );
}

// Full-screen shell for registry brokerage users. Brokers reach it from the app header (a
// "Brokerage admin" link) and can jump back; brokerage admins live here (console-only, no app data).
export default function BrokerConsole() {
  const { user, logout } = useAuth();
  const isBroker = user?.role === 'broker';
  const isAdmin = user?.role === 'brokerage_admin';
  const isManager = isBroker || isAdmin;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = () => authFetch(`${API}/api/associations/broker/overview`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
    .then(setData).catch((e) => setError(e.message));
  useEffect(() => { if (isManager) load(); }, [isManager]);

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      <nav className="bg-[#1A2744] border-b border-[#546E7A]/30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex justify-between items-center gap-2">
          <div className="text-xl min-w-0 truncate"><span className="font-bold text-white">Scheme</span><span className="font-bold text-[#00D4FF]">Sync</span><span className="font-bold text-[#00E676] ml-1.5 text-base">Brokerage</span></div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0 justify-end">
            {isBroker && <AppOpenActions user={user} />}
            <span className="hidden sm:block text-sm text-white truncate max-w-[12rem]">{user?.full_name}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-blue-600 text-blue-100 shrink-0">{isAdmin ? 'Brokerage Admin' : isBroker ? 'Broker' : 'Agent'}</span>
            <button onClick={logout} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-red-600/20 rounded-md shrink-0">Logout</button>
          </div>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {!isManager ? (
          <Card title={`Welcome, ${user?.full_name}`}>
            <p className="text-[#B0BEC5] text-sm">Your account is set up.</p>
          </Card>
        ) : error ? <p className="text-red-400">{error}</p> : !data ? <p className="text-[#B0BEC5]">Loading…</p> : <BrokerBody data={data} reload={load} currentUid={user?.id} isAdmin={isAdmin} />}
      </div>
    </div>
  );
}

function BrokerBody({ data, reload, currentUid, isAdmin }) {
  const s = data.seats;
  const [invites, setInvites] = useState([]);
  const addInvite = (name, path) => setInvites((v) => [{ name, link: `${window.location.origin}${path}` }, ...v]);
  return (
    <div className="space-y-4">
      {s.in_trial && (
        <div className="bg-blue-600/90 text-white rounded-lg px-4 py-2 text-sm font-medium">
          Trial — {s.trial_days_left} day{s.trial_days_left !== 1 ? 's' : ''} remaining. Billing starts {s.trial_end}.
        </div>
      )}
      <h1 className="text-white text-2xl font-bold">{s.name}</h1>
      <div className="grid md:grid-cols-2 gap-4">
        {s.licensed_re && <SeatCard label="Residential (RE)" used={s.used_re} cap={s.cap_re} product="re" reload={reload} />}
        {s.licensed_cre && <SeatCard label="Commercial (CRE)" used={s.used_cre} cap={s.cap_cre} product="cre" reload={reload} />}
      </div>
      <CreateUser seats={s} reload={reload} addInvite={addInvite} isAdmin={isAdmin} />
      {invites.length > 0 && (
        <Card title="Invite links (email is stubbed — send these)">
          {invites.map((iv, i) => (<div key={i} className="mb-2"><p className="text-xs text-[#B0BEC5] mb-1">{iv.name}</p><CopyRow value={iv.link} /></div>))}
        </Card>
      )}
      <Card title={`Users (${data.users.length})`}>
        {data.users.map((u) => <UserRow key={u.id} u={u} users={data.users} currentUid={currentUid} reload={reload} />)}
      </Card>
    </div>
  );
}

function SeatCard({ label, used, cap, product, reload }) {
  const [open, setOpen] = useState(false);
  const [n, setN] = useState(cap + 1);
  const [msg, setMsg] = useState('');
  const atCap = used >= cap;
  const request = async (e) => {
    e.preventDefault(); setMsg('');
    const res = await authFetch(`${API}/api/associations/broker/request-seats`, { method: 'POST', body: JSON.stringify({ product, requested_seats: Number(n) }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.detail || 'Failed'); return; }
    setMsg('Request sent to your association.'); setOpen(false); reload();
  };
  return (
    <Card title={label}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-white min-w-0 break-words">{used} / {cap} seats used {atCap && <span className="text-orange-400 text-xs ml-2">at cap</span>}</div>
        <button onClick={() => setOpen(!open)} className="px-3 py-1.5 text-sm text-[#00D4FF] hover:bg-[#243B55] rounded shrink-0">Request more</button>
      </div>
      {open && (
        <form onSubmit={request} className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-[#B0BEC5]">New total cap</span>
          <input type="number" min={cap + 1} value={n} onChange={(e) => setN(e.target.value)} className="w-24 px-2 py-1 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
          <button type="submit" className="px-3 py-1.5 text-sm bg-[#00E676] text-[#0A0E27] font-semibold rounded">Send request</button>
        </form>
      )}
      {msg && <p className="text-xs text-[#00E676] mt-2">{msg}</p>}
    </Card>
  );
}

function CreateUser({ seats, reload, addInvite, isAdmin }) {
  const [f, setF] = useState({ full_name: '', email: '', phone: '', role: 'agent',
    access_re: seats.licensed_re && !seats.licensed_cre, access_cre: seats.licensed_cre && !seats.licensed_re });
  const [err, setErr] = useState(''); const [open, setOpen] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const isBrokerageAdmin = f.role === 'brokerage_admin';
  const create = async (e) => {
    e.preventDefault(); setErr('');
    if (!isBrokerageAdmin && !(f.access_re || f.access_cre)) { setErr('Select at least one product.'); return; }
    const res = await authFetch(`${API}/api/associations/broker/users`, { method: 'POST', body: JSON.stringify(f) });
    const d = await res.json().catch(() => ({}));
    if (res.status === 409) { setErr(`No seats left for ${d.detail === 'AT_CAP_RE' ? 'RE' : 'CRE'}. Request more seats above, then try again.`); return; }
    if (!res.ok) { setErr(d.detail || 'Failed'); return; }
    addInvite(f.full_name, d.invite_path);
    setF({ ...f, full_name: '', email: '', phone: '' }); setOpen(false); reload();
  };
  return (
    <Card title="Add a user" right={<button onClick={() => setOpen(!open)} className="px-3 py-1.5 text-sm text-[#00D4FF] hover:bg-[#243B55] rounded">{open ? 'Close' : 'New user'}</button>}>
      {!open ? <p className="text-[#B0BEC5] text-sm">Create agents, brokers, and brokerage admins. Product access consumes one seat per product; a brokerage admin (back-office console access) consumes no seat and sees no app data.</p> : (
        <form onSubmit={create} className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <In label="Full name *" value={f.full_name} onChange={(v) => set('full_name', v)} required />
            <In label="Email *" type="email" value={f.email} onChange={(v) => set('email', v)} required />
            <In label="Phone" value={f.phone} onChange={(v) => set('phone', v)} />
            <div>
              <label className="block text-xs text-[#B0BEC5] mb-1">Role</label>
              <select value={f.role} onChange={(e) => set('role', e.target.value)} className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm">
                <option value="agent">Agent</option>
                {!isAdmin && <option value="broker">Broker</option>}
                <option value="brokerage_admin">Brokerage Admin — console only, no seat</option>
              </select>
            </div>
          </div>
          {isBrokerageAdmin ? (
            <p className="text-xs text-[#B0BEC5]">Brokerage admins manage users &amp; seats from this console only. No product access, no seat, no app data.</p>
          ) : (
            <div className="flex gap-4">
              {seats.licensed_re && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.access_re} onChange={(e) => set('access_re', e.target.checked)} /> RE seat</label>}
              {seats.licensed_cre && <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={f.access_cre} onChange={(e) => set('access_cre', e.target.checked)} /> CRE seat</label>}
            </div>
          )}
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="submit" className="px-4 py-2 rounded-md bg-[#00E676] text-[#0A0E27] font-semibold">Create &amp; Invite</button>
        </form>
      )}
    </Card>
  );
}

function UserRow({ u, users, currentUid, reload }) {
  const [off, setOff] = useState(false);
  const [target, setTarget] = useState('');
  const [msg, setMsg] = useState('');
  const peers = (users || []).filter((x) => x.is_active && x.id !== u.id && !x.invite_pending);
  const patch = async (body) => {
    const res = await authFetch(`${API}/api/associations/broker/users/${u.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert((d.detail === 'AT_CAP_RE' || d.detail === 'AT_CAP_CRE')
        ? 'No seats available for that product — free a seat or request more.' : (d.detail || 'Update failed'));
    }
    reload();
  };
  const offboard = async () => {
    setMsg('');
    const body = target ? { reassign_to_uid: Number(target) } : {};
    const res = await authFetch(`${API}/api/associations/broker/users/${u.id}/offboard`, { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.detail || 'Offboard failed'); return; }
    const m = d.moved || {};
    setMsg(d.reassigned
      ? `Deactivated. Reassigned ${m.buyers || 0} client${m.buyers === 1 ? '' : 's'} and ${m.properties || 0} propert${m.properties === 1 ? 'y' : 'ies'}. Seat freed.`
      : 'Deactivated — book left with this account (data retained). Seat freed.');
    setOff(false); reload();
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#546E7A]/20 text-sm last:border-0">
      <div className="min-w-0 break-words">
        <span className={u.is_active ? 'text-white' : 'text-gray-500 line-through'}>{u.full_name} <span className="text-gray-500">({u.email})</span></span>
        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-[#243B55] text-[#B0BEC5]">{u.role}</span>
        <span className="ml-1 text-xs text-[#B0BEC5]">{[u.access_re && 'RE', u.access_cre && 'CRE'].filter(Boolean).join('+') || '—'}</span>
        {u.invite_pending && <span className="ml-2 text-xs text-orange-400">invite pending</span>}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0 items-center">
        {u.is_active ? (
          <>
            <button onClick={() => patch({ is_active: false })} className="px-2 py-1 text-xs bg-red-600/80 text-white rounded">Deactivate</button>
            {u.id !== currentUid && !u.invite_pending &&
              <button onClick={() => setOff(!off)} className="px-2 py-1 text-xs bg-orange-600/80 text-white rounded">Offboard</button>}
          </>
        ) : (
          <button onClick={() => patch({ is_active: true })} className="px-2 py-1 text-xs bg-[#00E676] text-[#0A0E27] rounded">Reactivate</button>
        )}
      </div>
      {off && (
        <div className="w-full mt-2 bg-[#0A0E27] rounded p-2 border border-[#546E7A]/30 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#B0BEC5]">Reassign this user’s book to:</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            className="px-2 py-1 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white text-xs">
            <option value="">— Don’t reassign (leave with deactivated account) —</option>
            {peers.map((p) => <option key={p.id} value={p.id}>{p.full_name} ({p.role})</option>)}
          </select>
          <button onClick={offboard} className="px-3 py-1 text-xs bg-orange-600 text-white font-semibold rounded">Confirm offboard</button>
          <button onClick={() => { setOff(false); setMsg(''); }} className="px-2 py-1 text-xs text-[#B0BEC5]">Cancel</button>
        </div>
      )}
      {msg && <p className="w-full text-xs text-[#00E676] mt-1">{msg}</p>}
    </div>
  );
}
